import DeleteIcon from '@mui/icons-material/Delete'
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { InputLabel, MenuItem, Select } from '@mui/material'
import { isValidPhoneNumber } from 'libphonenumber-js'
import React, { useActionState, useEffect, useState } from 'react'
import { useFormStatus } from 'react-dom'
import { useTranslation } from 'react-i18next'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'

import { attachFiles, createZohoTicket } from '../api/services/apiService'
import { useAppSelector } from '../store/store.hooks'
import type { FormDataCustom, SupportTicketModalProps } from '../types'
import { useUserDetails, useUserSitesByOrg } from '../user/hooks/user.hooks'
import { logger } from '../utils/logger'
import {
  PhoneInput,
  defaultCountries,
  parseCountry,
} from 'react-international-phone';
import { RootState } from '../store/store'

const countries = defaultCountries.filter((country) => {
  const { iso2 } = parseCountry(country);
  return ['us', 'ca'].includes(iso2);
});

const SubmitButton = ({ disabled }: { disabled?: boolean }) => {
  const { pending } = useFormStatus()
  const { t } = useTranslation()
  return (
    <Button type="submit" variant="contained" disabled={pending || disabled}>
      {pending ? <CircularProgress size={16} color="inherit" /> : t('buttons.submit')}
    </Button>
  )
}

const SupportTicketModal = ({ onSubmit, onClose }: SupportTicketModalProps) => {
  const isAuthenticated = useAppSelector((state: RootState) => state.auth.isAuthenticated)
  const userDetails = useUserDetails()
  const { t } = useTranslation()

  const sites = useUserSitesByOrg()
  const allSites = sites.flatMap((org) => org.sites)

  const [formData, setFormData] = useState<FormDataCustom>({
    name: '',
    email: '',
    millName: '',
    phone: '',
    title: '',
    problemDetail: '',
    files: [],
  })

  const [descriptionError, setDescriptionError] = useState(false)
  const [phoneError, setPhoneError] = useState('')

  useEffect(() => {
    if (isAuthenticated && userDetails) {
      setFormData((prevState) => ({
        ...prevState,
        name: userDetails.fullName,
        email: userDetails.email,
      }))
    }
  }, [userDetails, isAuthenticated])

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prevState) => ({ ...prevState, [name]: value }))

  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files)
      setFormData((prevState) => ({
        ...prevState,
        files: [...prevState.files, ...newFiles],
      }))
    }
  }

  const handleProblemDetailChange = (value: string) => {
    setFormData((prevState) => ({ ...prevState, problemDetail: value }))
    setDescriptionError(!value)
  }

  const validatePhone = (value: string) => {
    if (!value) {
      setPhoneError('')
      return true
    }
    const isValid = isValidPhoneNumber(value)
    setPhoneError(isValid ? '' : t('userSettingsPage.invalidPhone'))
    return isValid
  }

  const handleDeleteFile = (index: number) => {
    setFormData((prevState) => ({
      ...prevState,
      files: prevState.files.filter((_, i) => i !== index),
    }))
  }

  const [serverError, submitAction, isPending] = useActionState(async (): Promise<string | null> => {
    if (!formData.problemDetail) {
      setDescriptionError(true)
      return null
    }

    if (formData.phone && !validatePhone(formData.phone)) {
      return null
    }

    try {
      const ticketId = await createZohoTicket(formData)
      if (formData.files.length > 0) {
        await attachFiles(ticketId, formData.files)
      }
      onSubmit(formData)
      onClose()
      return null
    } catch (error) {
      logger.error('Error creating ticket:', error)
      return t('supportForm.errorMessage')
    }
  }, null)

  return (
    <Box component="form" action={submitAction} sx={{ margin: 'auto', p: 3 }}>
      <Backdrop open={isPending} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Typography variant="h6" gutterBottom>
        {t('supportForm.title')}
      </Typography>
      <Grid id="support-ticket-form" container spacing={2} sx={{ mb: 2 }}>
        <Grid
          size={{
            xs: 12,
            md: 5,
          }}
        >
          <TextField
            fullWidth
            label={t('supportForm.name')}
            name="name"
            value={formData.name}
            onChange={handleTextChange}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('supportForm.email')}
            name="email"
            value={formData.email}
            onChange={handleTextChange}
            required
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel>{t('supportForm.millName')}</InputLabel>
            <Select
              value={formData.millName}
              label={t('supportForm.millName')}
              onChange={(e) => setFormData((prev) => ({ ...prev, millName: e.target.value }))}
            >
              {allSites.map((site) => (
                <MenuItem key={site.id} value={site.name}>
                  {site.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <PhoneInput
            defaultCountry="ca"
            value={formData.phone}
            onChange={(value) => {
              setFormData((prev) => ({ ...prev, phone: value }))
              validatePhone(value)
            }}
            countries={countries}
            style={{ width: '100%', marginBottom: phoneError ? '4px' : '16px' }}
            inputStyle={{
              width: '100%',
              height: '56px',
              fontSize: '1rem',
              borderRadius: '4px',
              border: phoneError ? '1px solid #d32f2f' : '1px solid rgba(0,0,0,0.23)',
            }}
            countrySelectorStyleProps={{
              buttonStyle: { height: '56px' },
            }}
          />
          {phoneError && (
            <Typography variant="caption" color="error" sx={{ mb: 2, display: 'block' }}>
              {phoneError}
            </Typography>
          )}

          <Button
            variant="contained"
            component="label"
            sx={{ mb: 2, textAlign: 'left', color: 'black', backgroundColor: 'white' }}
          >
            {t('supportForm.addFiles')}
            <input type="file" hidden multiple onChange={handleFileChange} />
          </Button>

          {formData.files.map((file, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="body1"
                sx={{ mr: 2, flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
                title={file.name}
              >
                {file.name}
              </Typography>
              <IconButton onClick={() => handleDeleteFile(index)} color="secondary">
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 7,
          }}
        >
          <TextField
            fullWidth
            label={t('supportForm.title')}
            name="title"
            value={formData.title}
            onChange={handleTextChange}
            required
            sx={{ mb: 2 }}
          />
          <Box sx={{ '& .ql-container': { minHeight: '200px' }, mb: 6 }}>
            <ReactQuill
              value={formData.problemDetail}
              onChange={handleProblemDetailChange}
              placeholder={t('supportForm.details')}
              modules={{
                toolbar: [
                  [{ header: '1' }, { header: '2' }, { font: [] }],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  ['bold', 'italic', 'underline'],
                  ['image'],
                  ['clean'],
                ],
                clipboard: {
                  matchVisual: false,
                },
              }}
              formats={['header', 'font', 'list', 'bold', 'italic', 'underline', 'image']}
            />
          </Box>
          {descriptionError && (
            <Typography variant="body2" color="error">
              {t('supportForm.descriptionRequired')}
            </Typography>
          )}
        </Grid>
      </Grid>
      {serverError && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {serverError}
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button variant="text" onClick={onClose} sx={{ color: 'black' }}>
          {t('buttons.cancel')}
        </Button>
        <SubmitButton disabled={!!phoneError} />
      </Box>
    </Box>
  )
}

export default SupportTicketModal
